import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { loginTeamMember, isTeamMemberAuthenticated } from '../services/teamAuthService';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(26, 31, 46, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 20px;
  padding: 48px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LogoText = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CompanyBadge = styled.div`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: center;
`;

const CompanyLabel = styled.div`
  color: #94a3b8;
  font-size: 12px;
  margin-bottom: 4px;
`;

const CompanyName = styled.div`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
  margin-bottom: 28px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  background: #252b3d;
  border: 1px solid #3a4055;
  border-radius: 10px;
  padding: 14px 16px;
  color: #fff;
  font-size: 15px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #64748b;
  border: none;
  padding: 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #94a3b8;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: #f87171;
  font-size: 14px;
  text-align: center;
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  color: #60a5fa;
  font-size: 13px;
  margin-top: 20px;
  text-align: center;
`;

export default function MemberLoginPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isLoading } = useAuth0();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect to home if already member authenticated
  useEffect(() => {
    if (isTeamMemberAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  // Redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await loginTeamMember(email, password);
      
      if (result.success) {
        // Redirect to main app
        navigate('/');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Card>
          <Logo>
            <LogoText>iMaven</LogoText>
          </Logo>
          <Subtitle>Loading...</Subtitle>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <Logo>
          <LogoText>iMaven</LogoText>
        </Logo>
        
        <CompanyBadge>
          <CompanyLabel>Company Account</CompanyLabel>
          <CompanyName>{user?.name || 'Logged In'}</CompanyName>
        </CompanyBadge>
        
        <Title>Who's working today?</Title>
        <Subtitle>Enter your team member credentials to continue</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Your Email</Label>
            <Input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoFocus
            />
          </FormGroup>

          <FormGroup>
            <Label>Your Password</Label>
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </FormGroup>

          <Button type="submit" disabled={loading || !email || !password}>
            {loading ? 'Signing in...' : 'Continue to Dashboard'}
          </Button>
        </Form>

        <InfoBox>
          🔐 This is a two-step login. Company auth ✓ Now verify your identity.
        </InfoBox>

        <SecondaryButton onClick={handleLogout} style={{ width: '100%', marginTop: 16 }}>
          Sign out of company account
        </SecondaryButton>
      </Card>
    </PageContainer>
  );
}
